import { FC, useCallback, useEffect, useState } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import useSwr, { mutate } from 'swr';
import toast from 'react-hot-toast';
import cn from 'clsx';

import { fetchPollDetails, FormattedPollDetail } from '@utils/Poll';
import { useAuth } from '@hooks/Auth';

import ButtonMighty from '@components/elements/ButtonMighty';
import HeaderImage from '@components/elements/HeaderImage';
import PollPeriodInput from '@components/modules/Poll/PeriodInput';
import PollQuestionCard from '@components/modules/Poll/QuestionCard';
import Toggle from '@components/elements/Toggle';
import Logo from '@images/logo.png';
import { voteSwingPolls } from '@sdk/managers/VotesManager';
import { mergeDateTime } from '@utils/Date';
import { addMinutes, addDays, isPast } from 'date-fns';

import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CREATE_SWING_VOTE_SCHEMA, CreateSwingVoteData } from './types';
import { PollStatus } from '@sdk/models';

const randomColors = [
  '#2EFFE4',
  '#FF46FF',
  '#FFB400',
  '#FF2300',
  '#FFFF00',
  '#8401FF',
  '#28FF2F',
].filter((item) => item !== localStorage.getItem('randomColor'))[
  Math.floor(Math.random() * (localStorage.getItem('randomColor') ? 6 : 7))
];
localStorage.setItem('randomColor', randomColors);

const PollSwingVotePage: FC = () => {
  const {
    params: { pollId },
  } = useRouteMatch<{
    pollId: string;
  }>();
  const history = useHistory<any>();

  const { user, dbUser, loading, company } = useAuth();
  const form = useForm<CreateSwingVoteData>({
    reValidateMode: 'onChange',
    resolver: zodResolver(CREATE_SWING_VOTE_SCHEMA),
    defaultValues: {
      period: {
        immediate: false,
        focusedInput: 'startDate',
        startDate: new Date(),
        startTime: addMinutes(new Date(), 1),
        endDate: addDays(new Date(), 7),
        endTime: addMinutes(new Date(), 10),
      },
    },
  });

  const { data, error } = useSwr<FormattedPollDetail>(
    pollId && loading === false
      ? ['/poll/detail', pollId, user === null]
      : null,
    fetchPollDetails,
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log('Failed to fetch poll details', { key }, err);
        toast.error(
          err?.message ||
            err?.errors?.[0]?.message ||
            'Failed to fetch poll details'
        );
      },
    }
  );

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Redirect if not owner
  useEffect(() => {
    if (loading) return;
    if (
      (user?.username !== undefined &&
        data?.poll.owner !== undefined &&
        user.username !== data.poll.owner) ||
      (data?.poll.companyID &&
        company?.companyID &&
        company.companyID !== data?.poll.companyID)
    ) {
      toast.error('You are not allowed to cast swing votes.');
      return history.replace(`/poll/${pollId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, data, loading, company, pollId]);

  useEffect(() => {
    const poll = data?.poll;

    if (poll) {
      if (!isPast(data.poll.endDate)) {
        toast.error('Poll has not ended voting period.');
        return history.replace(`/poll/${pollId}`);
      } else if (data.poll.status !== PollStatus.SWING) {
        toast.error('Poll does not require a Swing Vote.');
        return history.replace(`/poll/${pollId}`);
      }
    }

    // setup form
    if (data?.poll !== undefined) {
      form.reset({
        period: {
          immediate: false,
          focusedInput: 'startDate',
          startDate: data.poll.startDate,
          startTime: data.poll.startDate,
          endDate: data.poll.endDate,
          endTime: data.poll.endDate,
        },
      });
    }
  }, [data, form, history, pollId]);

  const onSubmit = useCallback(
    async (data: CreateSwingVoteData) => {
      console.log('on subimt', data);
      if (!user) {
        return history.push({
          pathname: '/login',
          state: { from: window.location.pathname },
        });
      }
      if (!dbUser) return;
      setIsSubmitting(true);

      try {
        if (data.selected === null) {
          throw new Error('You need to select an option');
        }
        let pledgePeriod = {};
        if (data.updatePeriod) {
          pledgePeriod = {
            pledgePeriod1StartTime: mergeDateTime(
              data.period.startDate,
              data.period.startTime
            ),
            pledgePeriod1EndTime: mergeDateTime(
              data.period.endDate,
              data.period.endTime
            ),
          };
        }
        console.log(pollId, dbUser.id, data.selected, pledgePeriod);
        const execute = async () => {
          const res = await voteSwingPolls(
            pollId,
            dbUser.id,
            data.selected,
            pledgePeriod
          );
          return res;
        };
        const res = await toast.promise(execute(), {
          loading: 'Casting Swing Vote...',
          success: 'Casted Swing Vote',
          error: (err) =>
            err.error || err.message || 'Failed to cast swing vote',
        });

        await Promise.all([
          mutate(['/poll/voted', pollId, dbUser?.id]),
          mutate(['/poll/detail', pollId, user === null]),
        ]);
        console.log(res);
        history.replace(`/poll/${pollId}`);
      } catch (err: any) {
        console.log('unexpected error submitting swing vote', err);
      }

      setIsSubmitting(false);
    },
    [user, dbUser, history, pollId]
  );

  let content;
  let leftContent;
  let header;
  if (error?.code === 'NotFound') {
    content = (
      <div className="pt-32">
        <div className="flex flex-col items-center justify-center px-4 py-24 mx-4 text-center bg-white shadow-r-salmon-md">
          <h1 className="text-3xl sm:text-4xl">Poll is no longer available.</h1>
          <p>The poll has been deleted or this is an invalid poll.</p>

          <div className="mt-24">
            <img src={Logo} alt="migthypoll" />
          </div>
        </div>
      </div>
    );
  } else {
    header = (
      <>
        <HeaderImage
          title={data?.poll.title || ''}
          titleSize={(data?.poll?.title?.length || 0) > 15 ? 40 : 55}
          image={data?.poll.image?.url || 'image'}
          alt="poll picture"
        />
      </>
    );
    leftContent = (
      <div className="flex flex-col mx-4 my-4 space-y-4 lg:mr-0 lg:ml-4">
        <div className="px-4 py-4 text-white bg-black">
          <h3 className="mb-2 text-lg text-white">Swing Vote</h3>
          <p className="pb-2">
            The poll ended in a tie, you will have a <u>Swing Vote</u>. It will
            determine the winner to proceed to pledging period.
          </p>
        </div>
        <div className="px-4 py-4 text-white bg-black">
          <h3 className="mb-2 text-lg text-white">Total Votes</h3>
          <div className="poll-detail-votes">
            <div>{data?.poll.voteCount || ''}</div>
            <div>Votes</div>
          </div>
        </div>
      </div>
    );

    content = (
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col mx-4 my-4 space-y-4 lg:mr-4 lg:ml-0">
          <div className="px-4 pt-4 pb-8 space-y-4 bg-black">
            {data?.poll && (
              <Controller
                name="selected"
                control={form.control}
                render={({ field: { value, ...field } }) => (
                  <PollQuestionCard
                    index={1}
                    neon={randomColors}
                    poll={data.poll}
                    vote={value}
                    onSelect={field.onChange}
                    selectable
                  />
                )}
              />
            )}
          </div>

          <div className="px-4 pt-4 pb-8 space-y-4 text-white bg-black">
            <h3 className="mb-2 text-3xl text-white">Submission</h3>
            <p className="pb-2">
              Optionally, you may modify your Pledge Period.
            </p>
            <Controller
              name="updatePeriod"
              control={form.control}
              render={({ field }) => (
                <>
                  <div
                    style={{ borderBottom: `2px solid ` + randomColors }}
                    className={cn(
                      'flex flex-row px-4 lg:px-4 py-4 my-6 items-between ',
                      {
                        'border-blue': field.value,
                      }
                    )}
                  >
                    <h3
                      className={cn('mr-4 text-lg w-full text-blue', {
                        'text-blue': field.value,
                      })}
                      style={{ color: randomColors }}
                    >
                      Update Pledge Period
                    </h3>
                    <div className="flex items-center">
                      <Toggle {...field} activeClassName="bg-blue" />
                    </div>
                  </div>
                  {field.value && (
                    <div className="pb-4">
                      <h3 className="mb-2 text-lg text-white">Pledge Period</h3>

                      <Controller
                        name="period"
                        control={form.control}
                        render={({ field: { value, ...field } }) => (
                          <PollPeriodInput
                            neon={randomColors}
                            isEdit={true}
                            onChange={field.onChange}
                            value={value}
                          />
                        )}
                      />
                    </div>
                  )}
                </>
              )}
            />

            <div
              className={cn('w-full xl:w-auto', {
                'opacity-60': form.watch('selected') === null,
              })}
            >
              <ButtonMighty
                neon={randomColors}
                style={{ background: randomColors, color: 'white' }}
                type="submit"
                disabled={isSubmitting || form.watch('selected') === null}
                loading={isSubmitting}
                color="#FFFF00"
                borderColor="#ff1dfc"
                textColor="#5600fb"
                text="Cast Swing Vote"
                margin="0"
              />
            </div>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div className="h-screen overflow-x-hidden overflow-y-scroll bg-fixed bg-black">
      {header}
      <div className="flex flex-col gap-4 mx-auto lg:container xl:gap-8 lg:flex-row">
        <div className="w-full lg:min-h-screen lg:sticky lg:top-0 lg:w-108">
          {leftContent}
        </div>
        <div className="flex flex-1">
          <div className="flex-1 pb-24">{content}</div>
        </div>
      </div>
    </div>
  );
};

export default PollSwingVotePage;
